/**
 * FREQUENTLY BOUGHT TOGETHER
 * ---------------------------------------------------------------------------
 * The markup and the matching are in snippets/handler-fbt.liquid, which picks
 * the partners server side. Nothing here fetches a product or a
 * recommendation; this file only runs the selection, the running total and the
 * one add to cart request.
 *
 * NO CUSTOM ELEMENT AND NO MODULE. The snippet renders at most once per page
 * and the script is only emitted when it does, so there is nothing to register
 * and nothing to import. A plain deferred script that wires one container is
 * the whole job.
 *
 * MONEY IS FORMATTED WITH THE SHOP'S OWN money_format, passed in on a data
 * attribute. Intl.NumberFormat was the first attempt and it disagrees with
 * Liquid's `money` filter in ways that show: the default currencyDisplay
 * prints "ZAR 1,475.00" where every other price on this store reads
 * "R 1,475.00". Reading the merchant's format means the total cannot drift
 * from the prices sitting directly above it.
 */
(function () {
  const root = document.querySelector('[data-handler-fbt]');
  if (!root) return;

  /*
   * ONE INITIALISATION PER ELEMENT. This script tag is rendered inside
   * handler-product-main, and the theme updates that section on a variant
   * change by MORPHING it (assets/section-renderer.js). A morph can re-insert
   * a script node, which re-executes it, and a second run would bind a second
   * change handler and a second click handler to the same button - so one
   * click would post the add twice.
   */
  if (root.dataset.fbtReady) return;
  root.dataset.fbtReady = '1';

  const items = Array.from(root.querySelectorAll('[data-fbt-item]'));
  if (!items.length) return;

  const totalEl = root.querySelector('[data-fbt-total]');
  const addButton = root.querySelector('[data-fbt-add]');
  const status = root.querySelector('[data-fbt-status]');
  const format = buildMoneyFormatter(root.dataset.moneyFormat);

  /* ---- total ------------------------------------------------------------ */

  function selected() {
    return items.filter((input) => input.checked);
  }

  function sync() {
    const chosen = selected();
    const cents = chosen.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);
    if (totalEl) totalEl.textContent = format(cents);
    addButton.disabled = chosen.length === 0;
  }

  root.addEventListener('change', (event) => {
    const target = event.target;

    // A variant select carries its own price on each option, so switching it
    // has to move both the id that will be added and the money on the card.
    if (target.matches('[data-fbt-variant]')) {
      const option = target.options[target.selectedIndex];
      const card = target.closest('.handler-fbt__item');
      const input = card && card.querySelector('[data-fbt-item]');
      if (input && option) {
        input.value = option.value;
        input.dataset.price = option.dataset.price || input.dataset.price;
        const priceEl = card.querySelector('.handler-fbt__price');
        if (priceEl) priceEl.textContent = format(Number(input.dataset.price) || 0);
      }
    }

    sync();
  });

  /* ---- the current product's own variant --------------------------------
     The buy column's variant picker can change after this section rendered,
     which would leave the first card holding a variant the shopper is no
     longer looking at.

     Most of the time the theme handles this without any help: a variant change
     re-fetches this whole section and morphs it in (assets/variant-picker.js
     into assets/section-renderer.js), so the card is re-rendered server side
     with the right id and the right price.

     This is the fallback for a change that does NOT morph the section - an app
     writing the hidden input directly, say. The theme keeps the live id in the
     add to cart form's hidden input, so that input is the source of truth.
     Whether a given change arrives as an attribute mutation or a change event
     is not worth predicting, so both are wired up and both are idempotent. */

  const LIVE_ID = '.handler-buy form[data-type="add-to-cart-form"] input[name="id"]';
  const selfInput = root.querySelector('[data-fbt-self]');
  const priceMap = readPriceMap(root);

  if (selfInput && priceMap) {
    const liveId = document.querySelector(LIVE_ID);

    if (liveId) {
      const followVariant = () => {
        const id = liveId.value;
        if (!id || !(id in priceMap)) return;
        selfInput.value = id;
        selfInput.dataset.price = String(priceMap[id]);
        const priceEl = root.querySelector('[data-fbt-self-price]');
        if (priceEl) priceEl.textContent = format(priceMap[id]);
        sync();
      };

      new MutationObserver(followVariant).observe(liveId, {
        attributes: true,
        attributeFilter: ['value'],
      });
      liveId.addEventListener('change', followVariant);
      followVariant();
    }
  }

  /* ---- add -------------------------------------------------------------- */

  addButton.addEventListener('click', async () => {
    const chosen = selected();
    if (!chosen.length || addButton.disabled) return;

    const label = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = 'Adding...';
    say('');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: chosen.map((input) => ({ id: variantFor(input), quantity: 1 })),
        }),
      });

      if (!response.ok) {
        // /cart/add.js answers a rejected line with a readable description,
        // which is more use to the shopper than "something went wrong".
        const problem = await response.json().catch(() => null);
        throw new Error((problem && (problem.description || problem.message)) || 'HTTP ' + response.status);
      }

      addButton.textContent = label;
      addButton.disabled = false;
      await showCart();
    } catch (error) {
      addButton.textContent = label;
      addButton.disabled = false;
      say(error.message || 'That did not add. Please try again.');
    }
  });

  /**
   * The variant to add for one card. For the partners the card's own value is
   * the whole story. For the current product it is read back off the buy
   * column at the moment of the click, not trusted from the observer above:
   * the wrong variant reaching the cart is the one failure here a shopper
   * would not notice until the order arrived.
   */
  function variantFor(input) {
    if (input.hasAttribute('data-fbt-self')) {
      const live = document.querySelector(LIVE_ID);
      const id = live && Number(live.value);
      if (id) return id;
    }
    return Number(input.value);
  }

  function say(message) {
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  /**
   * Re-renders the drawer through the Section Rendering API and opens it, the
   * same path snippets/cart-drawer.liquid and cart-upsell.js use. The theme's
   * own auto-open runs off Shopify's CartLinesUpdateEvent, which is dispatched
   * from a module this script cannot import, so the drawer is opened directly
   * instead of faking that event.
   */
  async function showCart() {
    try {
      const response = await fetch('/?section_id=cart-drawer-section');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer-component');
      const current = document.querySelector('cart-drawer-component');
      if (fresh && current) current.replaceWith(fresh);
    } catch (error) {
      /* the drawer just stays as it was; the line is in the cart either way */
    }

    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  function readPriceMap(scope) {
    const tag = scope.querySelector('[data-fbt-self-prices]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * Shopify's money_format, rendered. Handles the seven amount placeholders
   * Shopify supports; anything unrecognised falls through to a plain grouped
   * number so a bad format string cannot leave the total blank.
   */
  function buildMoneyFormatter(pattern) {
    const format = pattern || '${{amount}}';

    return function (cents) {
      const value = Number(cents) || 0;

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, placeholder) {
        switch (placeholder) {
          case 'amount':
            return group(value, 2, ',', '.');
          case 'amount_no_decimals':
            return group(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return group(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return group(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return group(value, 2, "'", '.');
          case 'amount_with_space_separator':
            return group(value, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return group(value, 0, ' ', ',');
          default:
            return group(value, 2, ',', '.');
        }
      });
    };
  }

  function group(cents, places, thousands, decimal) {
    const fixed = (cents / 100).toFixed(places);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return places && parts[1] ? whole + decimal + parts[1] : whole;
  }

  sync();
})();
